import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../service/apiService";
import apiServices from "../service";
import { RootState } from "./store";
import defaultTableData from "../shared/tableConfig.json";
import { getUserId } from "../utils";
import { MRT_SortingState, MRT_PaginationState } from "material-react-table";

/* =================== TYPES =================== */

export type TableName = "CLIENT_DETAILS" | "ACTION_LIST";

interface BaseTableFilter {
  [key: string]: any;
}

interface TableState<TFilter extends BaseTableFilter = BaseTableFilter> {
  draftFilters: TFilter;
  appliedFilters: TFilter;
  pagination: MRT_PaginationState;
  sorting: MRT_SortingState;
  columnOrder: string[];
  listData: {
    data: any[];
    total: number;
    page: number;
    limit: number;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
  };
  isFilterApplied: boolean;
  isInitialized: boolean;
}

interface ClientListFilter extends BaseTableFilter {
  search: string;
  scope: string[];
  client_type: string[];
  search_id: string;
  selection_ids: string[];
  value: any;
}

interface ActionListFilter extends BaseTableFilter {
  search: string;
  category: string[];
  dateCondition: any;
  consultant: string[];
  assignee: string[];
  status: string[];
}

type TableFilterMap = {
  CLIENT_DETAILS: ClientListFilter;
  ACTION_LIST: ActionListFilter;
};

interface PreferencesState {
  preferences: { tables: any };
  tableStates: { [K in TableName]: TableState<TableFilterMap[K]> };
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

/* =================== CONSTANTS =================== */

const API_BASE_URL = process.env.REACT_APP_BASE_URL + "user/profile";

export const DEFAULT_ACTION_CATEGORIES = [
  "asset_home_review",
  "credit_review_date",
  "mortgage_review_date",
  "loan_review_date",
  "policy_review_date",
  "client_review",
  "client_action",
  "asset_bank_review",
  "case_tracking_action",
  "asset_investment_review",
  "asset_shares_review",
  "birthday",
];
export const DEFAULT_ACTION_DATE = "next31DaysOverdue";
export const DEFAULT_ACTION_STATUS: string[] = [];

const defaultFilters: TableFilterMap = {
  CLIENT_DETAILS: {
    search: "",
    scope: [],
    client_type: [],
    search_id: "",
    selection_ids: [],
    value: null,
  },
  ACTION_LIST: {
    search: "",
    category: DEFAULT_ACTION_CATEGORIES,
    dateCondition: DEFAULT_ACTION_DATE,
    consultant: [],
    assignee: [],
    status: DEFAULT_ACTION_STATUS,
  },
};

const defaultPaginationConfig: Record<TableName, MRT_PaginationState> = {
  CLIENT_DETAILS: { pageIndex: 0, pageSize: 100 },
  ACTION_LIST: { pageIndex: 0, pageSize: 200 },
};

/* =================== HELPERS =================== */

/**
 * Deep-merges user_settings.tables from the API response with the local
 * defaultTableData so that any missing keys (null user_settings, missing
 * contextSpecific entries, missing filter/initialState sub-keys) always
 * fall back to the shipped defaults.
 *
 * Handles every null/missing scenario:
 *   - user_settings is null/undefined         → returns full defaults
 *   - user_settings.tables is null            → returns full defaults
 *   - contextSpecific[TABLE] missing          → uses default table config
 *   - filter keys missing inside a table      → filled from default filter
 *   - initialState keys missing inside table  → filled from default initialState
 *
 * Priority: user saved value > default value (per key)
 */
const mergeWithDefaults = (userTables: any): any => {
  const defaults = defaultTableData.tables;

  if (!userTables) return defaults;

  // Top-level defaultConfig — user wins per key, gaps filled from defaults
  const mergedDefaultConfig = {
    ...defaults.defaultConfig,
    ...(userTables.defaultConfig ?? {}),
  };

  // Merge each contextSpecific table so gaps inside a table also get defaults
  const mergedContextSpecific: any = {};

  (Object.keys(defaults.contextSpecific) as TableName[]).forEach((tableName) => {
    const defaultTable = (defaults.contextSpecific as any)[tableName];
    const userTable = userTables.contextSpecific?.[tableName];

    if (!userTable) {
      // Entire table missing from user_settings — use shipped default as-is
      mergedContextSpecific[tableName] = defaultTable;
      return;
    }

    // Merge filter keys so individual missing fields fall back to defaults
    const mergedFilter = {
      ...(defaultTable.filter ?? {}),
      ...(userTable.filter ?? {}),
    };

    // Merge initialState keys individually
    const mergedInitialState = {
      ...(defaultTable.initialState ?? {}),
      ...(userTable.initialState ?? {}),
      pagination: {
        ...(defaultTable.initialState?.pagination ?? {}),
        ...(userTable.initialState?.pagination ?? {}),
      },
      columnPinning: {
        ...(defaultTable.initialState?.columnPinning ?? {}),
        ...(userTable.initialState?.columnPinning ?? {}),
      },
    };

    mergedContextSpecific[tableName] = {
      ...defaultTable,
      ...userTable,
      filter: mergedFilter,
      initialState: mergedInitialState,
    };
  });

  return {
    ...defaults,
    ...userTables,
    defaultConfig: mergedDefaultConfig,
    contextSpecific: mergedContextSpecific,
  };
};

const getSavedTableState = (preferences: any, tableName: TableName): any => {
  const tableConfig = preferences?.tables?.contextSpecific?.[tableName];
  if (!tableConfig) return null;
  return {
    filter: tableConfig.filter ?? {},
    pagination: tableConfig.initialState?.pagination ?? defaultPaginationConfig[tableName],
    sorting: tableConfig.initialState?.sorting ?? [],
    columnOrder: tableConfig.initialState?.columnOrder ?? [],
  };
};

const resolveFilters = <TFilter extends BaseTableFilter>(
  uiFilter: TFilter,
  savedFilter: TFilter,
): any => {
  const resolved: any = {};

  for (const key in uiFilter) {
    const uiValue = uiFilter[key];
    const savedValue = savedFilter[key];

    if (Array.isArray(uiValue)) {
      resolved[key] = uiValue; // empty array = intentional clear
    } else if (typeof uiValue === "string") {
      resolved[key] = uiValue !== "" ? uiValue : savedValue || "";
    } else {
      resolved[key] = uiValue ?? savedValue;
    }
  }

  return resolved;
};

/* =================== HELPER TO CREATE TABLE STATE =================== */

const createTableState = <K extends TableName>(
  tableName: K,
): TableState<TableFilterMap[K]> => ({
  draftFilters: defaultFilters[tableName],
  appliedFilters: defaultFilters[tableName],
  pagination: defaultPaginationConfig[tableName],
  sorting: [],
  columnOrder: [],
  listData: {
    data: [],
    total: 0,
    page: 1,
    limit: defaultPaginationConfig[tableName].pageSize,
    status: "idle",
    error: null,
  },
  isFilterApplied: false,
  isInitialized: false,
});

/* =================== INITIAL STATE =================== */

const initialState: PreferencesState = {
  preferences: { tables: defaultTableData.tables },
  tableStates: {
    CLIENT_DETAILS: createTableState("CLIENT_DETAILS"),
    ACTION_LIST: createTableState("ACTION_LIST"),
  },
  status: "idle",
  error: null,
};

/* =================== ASYNC THUNKS =================== */

// NOTE: fetchUserPreferences has been removed.
// User preferences are now loaded as part of getUserProfileInfo in authSlice,
// which calls the same GET /user/profile/:userId endpoint and dispatches
// setPreferencesFromProfile — eliminating the duplicate API call.

export const saveTableState = createAsyncThunk<
  any,
  { tableName: TableName; shouldRefetch?: boolean }
>(
  "preferences/saveTableState",
  async (
    { tableName, shouldRefetch = false },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const tableState = state.preferences.tableStates[tableName];
      const currentPreferences = state.preferences.preferences;

      const updatedTableConfig = {
        ...currentPreferences.tables.contextSpecific[tableName],
        filter: tableState.appliedFilters,
        initialState: {
          ...currentPreferences.tables.contextSpecific[tableName].initialState,
          pagination: tableState.pagination,
          sorting: tableState.sorting,
          columnOrder: tableState.columnOrder,
        },
      };

      const updatedPreferences = {
        ...currentPreferences,
        tables: {
          ...currentPreferences.tables,
          contextSpecific: {
            ...currentPreferences.tables.contextSpecific,
            [tableName]: updatedTableConfig,
          },
        },
      };

      const userId = getUserId();
      const response = await axiosInstance.patch(`${API_BASE_URL}/${userId}`, {
        user_settings: updatedPreferences,
      });

      if (shouldRefetch) {
        dispatch(updatePreferencesOnly(updatedPreferences));
      }

      return { message: response.data.message, tableName };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resetTableStateToDefault = createAsyncThunk<
  any,
  { tableName: TableName }
>(
  "preferences/resetTableStateToDefault",
  async ({ tableName }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const currentPreferences = state.preferences.preferences;
      const defaultConfig = (defaultTableData.tables.contextSpecific as any)[tableName];

      const updatedPreferences = {
        ...currentPreferences,
        tables: {
          ...currentPreferences.tables,
          contextSpecific: {
            ...currentPreferences.tables.contextSpecific,
            [tableName]: defaultConfig,
          },
        },
      };

      const userId = getUserId();
      const response = await axiosInstance.patch(`${API_BASE_URL}/${userId}`, {
        user_settings: updatedPreferences,
      });

      dispatch(updatePreferencesOnly(updatedPreferences));
      return { message: response.data.message, tableName };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

/* =================== GENERIC FETCH DATA THUNK =================== */

export const transformClientData = (item: any) => ({
  customer_id: item.CustomerId,
  first_names: item.FirstNames,
  last_name: item.LastName,
  email: item.Email || "",
  client_type: item.ClientType,
  consultant_name: item.Consultant,
});

export const fetchTableData = createAsyncThunk<
  { data: any[]; total: number; page: number; limit: number },
  { tableName: TableName; params?: { page?: number; limit?: number } }
>(
  "preferences/fetchTableData",
  async ({ tableName, params = {} }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const tableState = state.preferences.tableStates[tableName];
      const savedState = getSavedTableState(state.preferences.preferences, tableName);

      const filters = resolveFilters(
        tableState.appliedFilters,
        savedState?.filter || defaultFilters[tableName],
      );

      const page = params.page ?? tableState.pagination.pageIndex + 1;
      const limit = params.limit ?? tableState.pagination.pageSize;

      if (tableName === "CLIENT_DETAILS") {
        if (filters.selection_ids?.length > 0) {
          const res = await apiServices.exportExcel.clientList(filters.selection_ids);
          return {
            data: res.data.data.map(transformClientData) ?? [],
            total: res.data?.count ?? 0,
            page: 1,
            limit: res.data?.count ?? filters.selection_ids.length,
          };
        }

        const res = await apiServices.client.getClientLists(
          page,
          limit,
          filters.search,
          filters.scope.join(","),
          filters.client_type.join(","),
          filters.search_id,
          filters.value,
        );

        return {
          data: res.data?.data?.customerList ?? [],
          total: res.data?.data?.count ?? 0,
          page,
          limit,
        };
      }

      if (tableName === "ACTION_LIST") {
        const userId = getUserId();
        // When a status filter is active and birthday is selected, ensure ""
        // is included so the backend returns null-status birthday records too.
        const activeStatus = filters.status ?? [];
        const statusFilter =
          activeStatus.length > 0 &&
          filters.category?.includes("birthday") &&
          !activeStatus.includes("")
            ? [...activeStatus, ""]
            : filters.status;
        const res = await apiServices.actionList.listItems(
          userId,
          page,
          limit,
          filters.dateCondition,
          filters.category,
          filters.consultant,
          filters.assignee,
          filters.search,
          statusFilter,
        );

        return {
          data: res.data?.data ?? [],
          total: res.data?.count ?? 0,
          page,
          limit,
        };
      }

      throw new Error(`Unknown table: ${tableName}`);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

/* =================== SLICE =================== */

const preferencesSlice = createSlice({
  name: "preferences",
  initialState,
  reducers: {
    setTableDraftFilter: (
      state,
      action: PayloadAction<{
        tableName: TableName;
        filter: Partial<BaseTableFilter>;
      }>,
    ) => {
      const { tableName, filter } = action.payload;
      Object.assign(state.tableStates[tableName].draftFilters, filter);
    },

    applyTableFilters: (
      state,
      action: PayloadAction<{ tableName: TableName }>,
    ) => {
      const { tableName } = action.payload;
      state.tableStates[tableName].appliedFilters = {
        ...state.tableStates[tableName].draftFilters,
      };
      state.tableStates[tableName].isFilterApplied = true;
      state.tableStates[tableName].pagination.pageIndex = 0;
    },

    setTablePagination: (
      state,
      action: PayloadAction<{
        tableName: TableName;
        pagination: MRT_PaginationState;
      }>,
    ) => {
      const { tableName, pagination } = action.payload;
      state.tableStates[tableName].pagination = pagination;
    },

    setTableSorting: (
      state,
      action: PayloadAction<{
        tableName: TableName;
        sorting: MRT_SortingState;
      }>,
    ) => {
      const { tableName, sorting } = action.payload;
      state.tableStates[tableName].sorting = sorting;
    },

    setTableColumnOrder: (
      state,
      action: PayloadAction<{ tableName: TableName; columnOrder: string[] }>,
    ) => {
      const { tableName, columnOrder } = action.payload;
      state.tableStates[tableName].columnOrder = columnOrder;
    },

    clearTableFilters: (
      state,
      action: PayloadAction<{ tableName: TableName }>,
    ) => {
      const { tableName } = action.payload;
      // Safe — mergeWithDefaults guarantees contextSpecific always has every table
      const savedConfig = state.preferences.tables.contextSpecific[tableName];
      const defaultFilter = defaultFilters[tableName];
      const defaultPagination = defaultPaginationConfig[tableName];

      state.tableStates[tableName].draftFilters = (savedConfig?.filter ?? defaultFilter) as any;
      state.tableStates[tableName].appliedFilters = (savedConfig?.filter ?? defaultFilter) as any;
      state.tableStates[tableName].pagination = savedConfig?.initialState?.pagination ?? defaultPagination;
      state.tableStates[tableName].sorting = savedConfig?.initialState?.sorting ?? [];
      state.tableStates[tableName].columnOrder = savedConfig?.initialState?.columnOrder ?? [];
      state.tableStates[tableName].isFilterApplied = false;
    },

    updatePreferencesOnly: (state, action: PayloadAction<any>) => {
      state.preferences = action.payload;
    },

    loadSavedTableState: (
      state,
      action: PayloadAction<{ tableName: TableName }>,
    ) => {
      const { tableName } = action.payload;
      const savedConfig = state.preferences.tables.contextSpecific[tableName];
      if (savedConfig) {
        const defaultFilter = defaultFilters[tableName];
        const savedFilter = savedConfig.filter ?? defaultFilter;
        state.tableStates[tableName].draftFilters = savedFilter;
        state.tableStates[tableName].appliedFilters = savedFilter;
        state.tableStates[tableName].pagination =
          savedConfig.initialState?.pagination ?? defaultPaginationConfig[tableName];
        state.tableStates[tableName].sorting = savedConfig.initialState?.sorting ?? [];
        state.tableStates[tableName].columnOrder = savedConfig.initialState?.columnOrder ?? [];
      }
    },

    /**
     * Called by authSlice's getUserProfileInfo after a successful profile fetch.
     *
     * Uses mergeWithDefaults to handle every null/missing scenario before
     * touching any table state — no individual null checks needed downstream.
     */
    setPreferencesFromProfile: (state, action: PayloadAction<any>) => {
      const userProfileInfo = action.payload;

      // Always produces a fully-populated tables object regardless of what
      // user_settings contains (null, partial, or complete)
      const mergedTables = mergeWithDefaults(
        userProfileInfo?.user_settings?.tables ?? null,
      );

      state.status = "succeeded";
      state.preferences = {
        ...state.preferences,
        tables: mergedTables,
      };

      // Hydrate each table state from the now-safe merged config
      (Object.keys(state.tableStates) as TableName[]).forEach((tableName) => {
        const table = state.tableStates[tableName];
        if (table.isInitialized) return;

        const savedConfig = mergedTables.contextSpecific?.[tableName];
        if (!savedConfig) return; // should never happen after mergeWithDefaults

        const defaultFilter = defaultFilters[tableName];
        table.draftFilters = (savedConfig.filter ?? defaultFilter) as any;
        table.appliedFilters = (savedConfig.filter ?? defaultFilter) as any;
        table.pagination = savedConfig.initialState?.pagination ?? defaultPaginationConfig[tableName];
        table.sorting = savedConfig.initialState?.sorting ?? [];
        table.columnOrder = savedConfig.initialState?.columnOrder ?? [];
        table.isInitialized = true;
      });
    },
  },

  extraReducers: (builder) => {
    builder
      // ─── resetTableStateToDefault ───────────────────────────────────────────
      .addCase(resetTableStateToDefault.fulfilled, (state, action) => {
        const tableName: TableName = action.payload.tableName;
        const defaultConfig = (defaultTableData.tables.contextSpecific as any)[tableName];
        const defaultFilter = defaultFilters[tableName];

        state.tableStates[tableName].draftFilters = (defaultConfig?.filter ?? defaultFilter) as any;
        state.tableStates[tableName].appliedFilters = (defaultConfig?.filter ?? defaultFilter) as any;
        state.tableStates[tableName].pagination =
          defaultConfig?.initialState?.pagination ?? defaultPaginationConfig[tableName];
        state.tableStates[tableName].sorting = defaultConfig?.initialState?.sorting ?? [];
        state.tableStates[tableName].columnOrder = defaultConfig?.initialState?.columnOrder ?? [];
        state.tableStates[tableName].isFilterApplied = false;
      })

      // ─── fetchTableData ─────────────────────────────────────────────────────
      .addCase(fetchTableData.pending, (state, action) => {
        const tableName = action.meta.arg.tableName;
        state.tableStates[tableName].listData.status = "loading";
      })
      .addCase(fetchTableData.fulfilled, (state, action) => {
        const tableName = action.meta.arg.tableName;
        state.tableStates[tableName].listData = {
          ...state.tableStates[tableName].listData,
          status: "succeeded",
          data: action.payload.data,
          total: action.payload.total,
          page: action.payload.page,
          limit: action.payload.limit,
        };
      })
      .addCase(fetchTableData.rejected, (state, action) => {
        const tableName = action.meta.arg.tableName;
        state.tableStates[tableName].listData.status = "failed";
        state.tableStates[tableName].listData.error = action.payload as string;
      });
  },
});

/* =================== SELECTORS =================== */

export const selectTableState = (tableName: TableName) => (state: RootState) =>
  state.preferences.tableStates[tableName];

export const selectTableDraftFilters =
  (tableName: TableName) => (state: RootState) =>
    state.preferences.tableStates[tableName].draftFilters;

export const selectTableAppliedFilters =
  (tableName: TableName) => (state: RootState) =>
    state.preferences.tableStates[tableName].appliedFilters;

export const selectTableAppliedFiltersJson =
  (tableName: TableName) => (state: RootState) =>
    JSON.stringify(state.preferences.tableStates[tableName].appliedFilters);

export const selectTablePagination =
  (tableName: TableName) => (state: RootState) =>
    state.preferences.tableStates[tableName].pagination;

export const selectTableSorting =
  (tableName: TableName) => (state: RootState) =>
    state.preferences.tableStates[tableName].sorting;

export const selectTableColumnOrder =
  (tableName: TableName) => (state: RootState) =>
    state.preferences.tableStates[tableName].columnOrder;

export const selectTableListData =
  (tableName: TableName) => (state: RootState) =>
    state.preferences.tableStates[tableName].listData;

export const selectIsTableFilterApplied =
  (tableName: TableName) => (state: RootState) =>
    state.preferences.tableStates[tableName].isFilterApplied;
export const selectIsDefaultPreference =
  (tableName: TableName) => (state: RootState) => {
    const savedConfig =
      state.preferences.preferences.tables.contextSpecific?.[tableName];

    const defaultConfig =
      defaultTableData.tables.contextSpecific?.[tableName];

    if (!savedConfig || !defaultConfig) return true;

    const savedFilter = savedConfig.filter;
    const defaultFilter = defaultConfig.filter;

    const savedPagination = savedConfig.initialState?.pagination;
    const defaultPagination = defaultConfig.initialState?.pagination;

    const savedSorting = savedConfig.initialState?.sorting;
    const defaultSorting = defaultConfig.initialState?.sorting;

    const savedColumnOrder = savedConfig.initialState?.columnOrder;
    const defaultColumnOrder = defaultConfig.initialState?.columnOrder;

    const isFilterEqual =
      JSON.stringify(savedFilter) === JSON.stringify(defaultFilter);

    const isPaginationEqual =
      JSON.stringify(savedPagination) === JSON.stringify(defaultPagination);

    const isSortingEqual =
      JSON.stringify(savedSorting) === JSON.stringify(defaultSorting);

    const isColumnOrderEqual =
      JSON.stringify(savedColumnOrder) === JSON.stringify(defaultColumnOrder);

    return (
      isFilterEqual &&
      isPaginationEqual &&
      isSortingEqual &&
      isColumnOrderEqual
    );
  };

export const selectHasTemporaryChanges =
  (tableName: TableName) => (state: RootState) => {
    const tableState = state.preferences.tableStates[tableName];
    const savedConfig =
      state.preferences.preferences.tables.contextSpecific?.[tableName];

    const defaultFilter = defaultFilters[tableName];
    const defaultPagination = defaultPaginationConfig[tableName];

    const savedFilter = savedConfig?.filter ?? defaultFilter;
    const savedPagination = savedConfig?.initialState?.pagination ?? defaultPagination;
    const savedSorting = savedConfig?.initialState?.sorting ?? [];
    const savedColumnOrder = savedConfig?.initialState?.columnOrder ?? [];

    return (
      JSON.stringify(tableState.appliedFilters) !== JSON.stringify(savedFilter) ||
      tableState.pagination.pageIndex !== savedPagination.pageIndex ||
      tableState.pagination.pageSize !== savedPagination.pageSize ||
      JSON.stringify(tableState.sorting) !== JSON.stringify(savedSorting) ||
      JSON.stringify(tableState.columnOrder) !== JSON.stringify(savedColumnOrder)
    );
  };

/* =================== EXPORTS =================== */

export const {
  setTableDraftFilter,
  applyTableFilters,
  setTablePagination,
  setTableSorting,
  setTableColumnOrder,
  clearTableFilters,
  loadSavedTableState,
  updatePreferencesOnly,
  setPreferencesFromProfile,
} = preferencesSlice.actions;

export default preferencesSlice.reducer;